import {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { onCreateFolder, onRenderFolders, uploadFile } from "../lib/files";
import BusinessContext from "./business-context";
import { useRouter } from "next/router";
import { onCreateRow, OnCreateTable, renderPegs, renderRows, renderTables, updateRow } from "../lib/table";
import { set } from "date-fns";


const MailContext = createContext({
  isComposeMailOpen: false,
  setIsComposeMailOpen: () => { },
  activeInbox: null,
  setActiveInbox: () => { },
  isMailFullScreen: false,
  setIsMailFullScreen: () => { },
  uploadAttachment: () => { },
  attachments: null,
  setAttachments: () => { },
  uploadingAttachments: [],
  attachmentErrors: [],
  clearAttachmentError: () => { },
  showBlog: false,
  setShowBlog: () => { },
  leftPanel: null,
  setLeftPanel: () => { },
  readEmail: () => { },
  mailForwardContent: {
    isActive: false,
    subject: '',
    body: '',
    recipients: [],
    cc: [],
    bcc: [],
    attachments: [],
    from: '',
    date: null,
    to: '',
  },
  setMailForwardContent: () => { },
  closeComposeMail: () => { },
  readEmailsRows: [],
  readEmailsTable: null,
});

export const MailProvider = (props) => {
  const { activeBusiness } = useContext(BusinessContext);
  const router = useRouter();
  const [showBlog, setShowBlog] = useState(true);
  const [isComposeMailOpen, setIsComposeMailOpen] = useState(false);
  const [activeInbox, setActiveInbox] = useState(null);
  const [isMailFullScreen, setIsMailFullScreen] = useState(false);
  const [path, setPath] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState([]);
  const [attachmentErrors, setAttachmentErrors] = useState([]);
  const [leftPanel, setLeftPanel] = useState(40);
  const [readEmailsTable, setReadEmailsTable] = useState(null);
  const [readEmailsRows, setReadEmailsRows] = useState([]);
  const [peg, setPeg] = useState(null);
  const [mailForwardContent, setMailForwardContent] = useState({
    isActive: false,
    subject: '',
    body: '',
    recipients: [],
    cc: [],
    bcc: [],
    attachments: [],
    from: '',
    date: new Date(),
    to: '',
  });

  useEffect(() => {
    // Reset state when the component mounts
    setIsComposeMailOpen(false);
    setActiveInbox(null);
    setIsMailFullScreen(false);
    setAttachments([]);
    setUploadingAttachments([]);
    setAttachmentErrors([]);
    setMailForwardContent({
      isActive: false,
      subject: '',
      body: '',
      recipients: [],
      cc: [],
      bcc: [],
      attachments: [],
      from: '',
      date: new Date(),
      to: '',
    });
    if (!peg) {
      getPeg();
    }
  }, [router, peg]);

  const onError = (error) => {
    console.error("Error:", error);
  };

  const closeComposeMail = () => {
    setIsComposeMailOpen(false);
    setMailForwardContent({
      isActive: false,
      subject: '',
      body: '',
      recipients: [],
      cc: [],
      bcc: [],
      attachments: [],
      from: '',
      date: new Date(),
      to: '',
    });
    setAttachments([]);
    setUploadingAttachments([]);
    setAttachmentErrors([]);
  }

  const clearAttachmentError = (fileName) => {
    setAttachmentErrors(prev => prev.filter(error => error.fileName !== fileName));
  };

  const uploadAttachment = (files) => {
    // Track files that are being uploaded
    const filesToUpload = files.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type
    }));

    setUploadingAttachments(prev => [...prev, ...filesToUpload]);

    const pathpayload = {
      namespace: "__emails",
      parent: null
    };

    const onError = (error, file) => {
      console.error("Error uploading attachment:", error);
      setAttachmentErrors(prev => [...prev, {
        fileName: file.name,
        error: error.message || "Failed to upload file"
      }]);

      // Remove from loading state
      setUploadingAttachments(prev =>
        prev.filter(item => item.name !== file.name)
      );
    };

    const uploadCallback = (json, file) => {
      setAttachments(prev => [...(prev || []), json]);

      // Remove from loading state
      setUploadingAttachments(prev =>
        prev.filter(item => item.name !== file.name)
      );
    };

    const createPathCallback = (json) => {
      setPath(json);
      files.forEach(file => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('path', json.id);
        formData.append('alias', file.name);

        uploadFile(
          formData,
          (result) => uploadCallback(result, file),
          (error) => onError(error, file),
          activeBusiness,
          router
        );
      });
    };

    const callBack = (json) => {
      const emailPath = json.find(item => item.namespace === "__emails");
      if (emailPath) {
        setPath(emailPath);
        files.forEach(file => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('path', emailPath.id);
          formData.append('alias', file.name);

          uploadFile(
            formData,
            (result) => uploadCallback(result, file),
            (error) => onError(error, file),
            activeBusiness,
            router
          );
        });
      }
      else {
        onCreateFolder(
          pathpayload,
          createPathCallback,
          activeBusiness,
          (error) => onError(error, files[0]),
          router
        );
      }
    };

    onRenderFolders(callBack, activeBusiness, (error) => onError(error, files[0]), router);
  };

  const getPeg = () => {
    const callback = (json) => {
      setPeg(json[0]);
    }
    const onError = (error) => {
      console.error("Error fetching peg:", error);
    };
    renderPegs(callback, "emails", onError, router);
  };

  const createTable = (type, emailId) => {
    const callback = (json) => {
      const table = json;
      setReadEmailsTable(table);
      
      // Now mark the email as read with the newly created table
      if (emailId) {
        const emailIdColumnId = table.columns.find(column => column.name === 'Email Id')?.id;
        const readAtColumnId = table.columns.find(column => column.name === 'Read At')?.id;
        
        const newrowData = {
          data: {
            [emailIdColumnId]: emailId,
            [readAtColumnId]: Date.now()
          },
          position: 1000.0,
          inherit_business_permission: true,
          inherit_individual_permissions: true,
        };

        const rowCallback = (json) => {
          setReadEmailsRows(prev => [...prev, json]);
        };
        
        onCreateRow(table.uuid, rowCallback, newrowData, activeBusiness, onError, router);
      }
    }

    const onError = (json) => {
      console.error(json);
    };

    let schema;
    const baseSchema = {
      "owner": 1,
      "business_permission": 4,
      "individual_permissions": [],
      "public_permission": 0,
      "namespace": type,
      "peg": peg ? peg.id : null
    };


    if (type === 'read_emails') {
      schema = {
        ...baseSchema,
        "name": "Read emails",
        "columns": [
          {
            "name": "Email Id",
            "type": "text",
            "position": 1000.0,
          },
          {
            "name": "Read At",
            "type": "number",
            "position": 2000.0
          }
        ],
        "metadata": {}
      };
    }
    OnCreateTable(schema, callback, activeBusiness, onError, router);
  }

  const createReadEmailRow = (emailId, readAt) => {

    const emailIdColumnId = readEmailsTable?.columns.find(column => column.name === 'Email Id')?.id;
    const readAtColumnId = readEmailsTable?.columns.find(column => column.name === 'Read At')?.id;


    const newrowData = {
      data: {
      [emailIdColumnId]: emailId,
      [readAtColumnId]: readAt
      },
      position: readEmailsRows.length > 0 ? Math.max(...readEmailsRows.map(row => row.position)) + 1000.0 : 1000.0,
      inherit_business_permission: true,
      inherit_individual_permissions: true,
    };

    const callBack = (json) => {
      setReadEmailsRows(prev => [...prev, json]);
    };
    onCreateRow(readEmailsTable?.uuid, callBack, newrowData, activeBusiness, onError, router);
  }
  const updateReadEmailRow = (emailId, readAt) => {
    const emailIdColumnId = readEmailsTable.columns.find(column => column.name === 'Email Id')?.id;
    const readAtColumnId = readEmailsTable.columns.find(column => column.name === 'Read At')?.id;

    const existingrow = readEmailsRows.find(row => row.data[emailIdColumnId] === emailId);

    const updatedrowData = {
      ...existingrow,
      data: {
        ...existingrow.data,
        [readAtColumnId]: readAt
      }
    };

    const callBack = (json) => {
      setReadEmailsRows(prev => prev.map(row => row.uuid === json.uuid ? json : row));
    };

    updateRow(readEmailsTable?.uuid, existingrow.id, updatedrowData, callBack, activeBusiness, onError, router);
  }

  const markEmailAsRead = (emailId) => {
    const emailIdColumnId = readEmailsTable?.columns.find(column => column.name === 'Email Id')?.id;
    const hasExistingrow = readEmailsRows.find(row => row.data[emailIdColumnId] === emailId);
    if (hasExistingrow) {
      updateReadEmailRow(emailId, Date.now());
    } else {
      createReadEmailRow(emailId, Date.now());
    }
  }

  const readEmail = (emailId) => {
    if (!readEmailsTable) {
      createTable('read_emails', emailId);
    } else {
      markEmailAsRead(emailId);
    }
  }

  const fetchReadEmailsTable = () => {
    const callback = (json) => {
      setReadEmailsTable(json[0]);
    };

    renderTables(callback, activeBusiness, onError, router, 'emails', null, "read_emails");
  } 

  const renderReadEmailsRows = () => {
    const callback = (json) => {
      setReadEmailsRows(json);
    };

    renderRows(readEmailsTable?.uuid, callback, activeBusiness, onError, router);
  }

  useEffect(() => {
    if (readEmailsTable) {
      renderReadEmailsRows();
    }
  }, [readEmailsTable]);

  useEffect(() => {
    if (activeBusiness) {
      fetchReadEmailsTable();
    }
  }, [activeBusiness]);

  const context = {
    isComposeMailOpen,
    setIsComposeMailOpen,
    activeInbox,
    setActiveInbox,
    isMailFullScreen,
    setIsMailFullScreen,
    uploadAttachment,
    attachments,
    setAttachments,
    uploadingAttachments,
    attachmentErrors,
    clearAttachmentError,
    mailForwardContent,
    setMailForwardContent,
    closeComposeMail,
    showBlog,
    setShowBlog,
    leftPanel,
    setLeftPanel,
    readEmail,
    readEmailsRows,
    readEmailsTable,
  };

  return (
    <MailContext.Provider value={context}>
      {props.children}
    </MailContext.Provider>
  );
};

export default MailContext;
